<script setup lang="ts">
import { ref, onMounted } from 'vue';
import InputGroupAddon from "primevue/inputgroupaddon";
import InputGroup from "primevue/inputgroup";
import InputNumber from "primevue/inputnumber";
import backendCall from "../../services/AxiosService";
import { useToast } from "primevue/usetoast";

const props = defineProps({clipNumber: String, eventId: Number, currentFuelLevel: String});
const emit = defineEmits(['update-clips']);
const refuelClipDialogVisible = defineModel<boolean>();
const toast = useToast();

const refuelFormObject = ref({
  eventId: props.eventId,
  clipNumber: props.clipNumber,
  fuelLevel: null,
  fuelAdded: null,
  notes: null,
  startFuelLevel: null,
  truckId: null as number | null,
});

const fuelOptions = ref(['E', '1/8', '1/4', '3/8', '5/16', '7/16', '1/2', '9/16', '5/8', '11/16', '3/4', '13/16', '7/8', 'F']);

const askForStartFuelLevel = ref(false);

const submitClipRefuel = async () => {
  try {
    if (!refuelFormObject.value.fuelLevel) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a fuel level',
        life: 3000
      });
      return;
    }

    if (!refuelFormObject.value.fuelAdded) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please enter the amount of fuel added',
        life: 3000
      });
      return;
    }

    // If we're asking for start fuel level, make sure it's provided
    if (askForStartFuelLevel.value && !refuelFormObject.value.startFuelLevel) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select a starting fuel level',
        life: 3000
      });
      return;
    }

    // Require truck number when fuel is added (DB constraint)
    if (Number(refuelFormObject.value.fuelAdded) > 0 && !refuelFormObject.value.truckId) {
      toast.add({
        severity: 'error',
        summary: 'Truck Required',
        detail: 'Please enter the fuel truck number used for this refuel',
        life: 5000
      });
      return;
    }

    try {
      const clipRefuelCall = await backendCall.post('clip-ons/refuel', refuelFormObject.value);
      if (!clipRefuelCall) {
        throw new Error('Failed to refuel clip-on');
      }

      refuelClipDialogVisible.value = false;
      emit('update-clips');

      toast.add({
        severity: 'success',
        summary: 'Success!',
        detail: `${props.clipNumber} has been refueled with ${refuelFormObject.value.fuelAdded} liters to level ${refuelFormObject.value.fuelLevel}`,
        life: 3000
      });
    } catch (error: any) {
      // Specific backend hint: starting fuel level missing
      if (error?.response?.data?.error?.code === 'NO_STARTING_FUEL_LEVEL') {
        askForStartFuelLevel.value = true;
        toast.add({
          severity: 'info',
          summary: 'Starting Fuel Level Required',
          detail: 'Please select the starting fuel level for this clip-on',
          life: 5000
        });
        return;
      }
      // Show backend message if available
      const backendMsg = error?.response?.data?.error?.message;
      if (backendMsg) {
        toast.add({ severity: 'error', summary: 'Refuel Failed', detail: backendMsg, life: 6000 });
        return;
      }
      // Fallback generic error
      throw error;
    }
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'An error occurred while trying to refuel the clip-on.',
      life: 5000
    });
  }
};

// Auto-fill truck number from Samsara assignment, when available
onMounted(async () => {
  try {
    const response = await backendCall.get('user/vehicle-assignment');
    const truck = response?.data?.data?.vehicleAssignment;
    if (truck) {
      const parsed = parseInt(String(truck));
      if (!isNaN(parsed)) {
        refuelFormObject.value.truckId = parsed;
      }
    }
  } catch (e) {
    // Non-fatal if we cannot auto-resolve the truck
  }
});
</script>

<template>
  <Dialog v-model:visible="refuelClipDialogVisible" appendTo="body" modal
          :breakpoints="{'960px': '75vw', '640px': '100vw'}" :style="{width: '30vw'}"
          :draggable="false" :resizable="false">
    <template #header>
      <h1 class="m-0 text-surface-900 dark:text-surface-0 font-semibold text-xl leading-normal">Refuel {{ clipNumber }}</h1>
    </template>
    <form class="flex flex-col gap-4 mt-4">
      <div v-if="currentFuelLevel" class="p-4 bg-surface-100 dark:bg-surface-800 rounded-lg">
        <p class="text-surface-700 dark:text-surface-300">Current Fuel Level: 
          <span :class="{'text-red-500': currentFuelLevel === 'E' || currentFuelLevel === '1/8' || currentFuelLevel === '1/4' || currentFuelLevel === '3/8' || currentFuelLevel === '5/16' || currentFuelLevel === '7/16' || currentFuelLevel === '1/2' || currentFuelLevel === '9/16' || currentFuelLevel === '5/8' || currentFuelLevel === '11/16' || currentFuelLevel === '3/4' || currentFuelLevel === '13/16'}">
            {{ currentFuelLevel }}
          </span>
        </p>
      </div>

      <!-- Starting Fuel Level input (shown only when needed) -->
      <div v-if="askForStartFuelLevel" class="w-full">
        <label for="start-fuel-level" class="block mb-1 text-color text-base">Starting Fuel Level</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-gauge text-lg"></i>
          </InputGroupAddon>
          <Dropdown id="start-fuel-level" v-model="refuelFormObject.startFuelLevel" :options="fuelOptions" placeholder="Select starting fuel level"/>
        </InputGroup>
        <small class="text-surface-600 dark:text-surface-400">Please select the current fuel level before refueling</small>
      </div>

      <div class="w-full">
        <label for="fuel-level" class="block mb-1 text-color text-base">New Fuel Level</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-gauge text-lg"></i>
          </InputGroupAddon>
          <Dropdown id="fuel-level" v-model="refuelFormObject.fuelLevel" :options="fuelOptions.toReversed()"/>
        </InputGroup>
      </div>

      <div class="w-full">
        <label for="fuel-added" class="block mb-1 text-color text-base">Fuel Added (liters)</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-tint text-lg"></i>
          </InputGroupAddon>
          <InputNumber id="fuel-added" v-model="refuelFormObject.fuelAdded" placeholder="Enter amount" :min="0" :step="0.1" />
        </InputGroup>
      </div>

      <!-- Truck number is required by DB when fuel is dispensed -->
      <div v-if="refuelFormObject.fuelAdded && Number(refuelFormObject.fuelAdded) > 0" class="w-full">
        <label for="truck-id" class="block mb-1 text-color text-base">Fuel Truck Number</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-truck text-lg"></i>
          </InputGroupAddon>
          <InputNumber id="truck-id" v-model="refuelFormObject.truckId" placeholder="Enter truck number" :min="1" />
        </InputGroup>
        <small class="text-surface-600 dark:text-surface-400">Required when recording fuel added</small>
      </div>

      <div class="flex gap-4">
        <div class="w-full">
          <label for="notes" class="block mb-1 text-color text-base">Notes</label>
          <Textarea class="w-full" id="notes" name="notes" v-model="refuelFormObject.notes"></Textarea>
        </div>
      </div>
    </form>
    <template #footer>
      <div class="flex gap-4 justify-end border-t border-surface pt-8">
        <Button label="Cancel" @click="refuelClipDialogVisible = false" class="p-button-text"></Button>
        <Button label="Refuel" @click="submitClipRefuel" class="p-button-rounded"></Button>
      </div>
    </template>
  </Dialog>
</template>
